import React, { useState } from 'react';
import { 
  Heart, MessageCircle, Users, MapPin, Sparkles, 
  BookOpen, Shield, ChevronRight, ChevronLeft, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingTutorial = ({ user, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  const slides = [
    {
      icon: Heart,
      color: 'from-[#FFB5A7] to-[#FCD5CE]',
      title: `Bienvenue ${user?.first_name || ''} ! 💕`,
      subtitle: 'Tu n\'es plus seule',
      description: 'Ici, tu rejoins une communauté de mamans bienveillantes qui comprennent ce que tu vis. Chaque jour, des mamans comme toi partagent, s\'entraident et se soutiennent. Tu es au bon endroit.',
      emoji: '🤗'
    },
    {
      icon: Users,
      color: 'from-[#A2D2FF] to-[#BDE0FE]',
      title: 'Trouve ton match',
      subtitle: 'Une amie qui te comprend vraiment',
      description: 'Réponds au questionnaire pour trouver la maman qui partage tes valeurs et ton quotidien. Crée une amitié authentique. Parfois, une seule rencontre peut tout changer.',
      emoji: '👯‍♀️'
    },
    {
      icon: MessageCircle,
      color: 'from-[#B5E48C] to-[#D8F3DC]',
      title: 'Pose tes questions',
      subtitle: 'Sans jugement, jamais',
      description: 'Tu doutes ? Tu as peur ? C\'est normal. Ici, chaque question est légitime et chaque maman peut t\'apporter son soutien. On est toutes passées par là, et on est là pour toi.',
      emoji: '💬'
    },
    {
      icon: BookOpen,
      color: 'from-[#CDB4DB] to-[#E2D1F9]',
      title: 'Astuces & Témoignages',
      subtitle: 'Tu fais déjà de ton mieux',
      description: 'Découvre des conseils de mamans pour les mamans, et des témoignages qui te rappelleront que tu n\'es pas seule. Chaque parcours est unique, et le tien est précieux.',
      emoji: '📖'
    },
    {
      icon: MapPin,
      color: 'from-[#FFD6A5] to-[#FFE5B4]',
      title: 'Autour de toi',
      subtitle: 'Des ressources près de chez toi',
      description: 'Pédiatres, PMI, associations de soutien... Trouve facilement les services et les professionnels dont tu as besoin, près de chez toi.',
      emoji: '📍'
    },
    {
      icon: Shield,
      color: 'from-[#98D8C8] to-[#B8E8DC]',
      title: 'Un cocon protégé',
      subtitle: 'Bienveillance garantie',
      description: 'Ici, pas de jugement, pas de compétition entre mamans. Notre charte assure un espace où tu peux être toi-même, avec tes doutes et tes victoires. Tu es en sécurité.',
      emoji: '🛡️'
    },
    {
      icon: Sparkles,
      color: 'from-[#FFB5A7] to-[#A2D2FF]',
      title: 'Tu es prête ! ✨',
      subtitle: 'On est là pour toi',
      description: 'Explore à ton rythme, sans pression. N\'oublie pas, tu es une super maman même les jours où tu en doutes. Bienvenue dans la communauté MamaLink.',
      emoji: '🎉'
    }
  ];

  const handleComplete = async () => {
    setIsClosing(true);
    
    try {
      const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
      if (token) {
        await fetch(`${BACKEND_URL}/api/user/complete-onboarding`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.log('Error completing onboarding:', error);
    }
    
    // Petit délai pour l'animation de fermeture
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  const handleNext = () => {
    if (currentStep < slides.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentSlide = slides[currentStep];
  const IconComponent = currentSlide.icon;
  const isLastSlide = currentStep === slides.length - 1;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div 
        className={`relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden transition-transform duration-300 ${isClosing ? 'scale-95' : 'scale-100'}`}
        data-testid="onboarding-modal"
      >
        {/* Header with gradient */}
        <div className={`bg-gradient-to-br ${currentSlide.color} p-8 pb-12 text-center relative`}>
          {/* Decorative circles */}
          <div className="absolute top-4 left-4 w-16 h-16 rounded-full bg-white/10" />
          <div className="absolute bottom-8 right-8 w-24 h-24 rounded-full bg-white/10" />
          <div className="absolute top-12 right-12 w-8 h-8 rounded-full bg-white/20" />
          
          {/* Icon */}
          <div className="relative z-10 w-20 h-20 mx-auto bg-white rounded-2xl shadow-lg flex items-center justify-center mb-4">
            <IconComponent className="w-10 h-10 text-[#5C3D36]" />
          </div>
          
          {/* Emoji */}
          <span className="text-4xl">{currentSlide.emoji}</span>
        </div>

        {/* Content */}
        <div className="p-6 text-center -mt-6 relative z-10">
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-[#F8EDEB]">
            <h2 className="text-2xl font-bold text-[#5C3D36] mb-2 font-serif">
              {currentSlide.title}
            </h2>
            <p className="text-[#FFB5A7] font-semibold text-sm mb-3">
              {currentSlide.subtitle}
            </p>
            <p className="text-[#9D8189] leading-relaxed">
              {currentSlide.description}
            </p>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? 'w-6 bg-[#FFB5A7]' 
                  : index < currentStep 
                    ? 'bg-[#B5E48C]' 
                    : 'bg-[#F8EDEB]'
              }`}
              data-testid={`onboarding-dot-${index}`}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between p-4 pt-0">
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={`flex items-center gap-1 px-4 py-2 rounded-full font-medium transition-all ${
              currentStep === 0 
                ? 'text-[#C9ADA7] cursor-not-allowed' 
                : 'text-[#9D8189] hover:bg-[#F8EDEB]'
            }`}
            data-testid="onboarding-prev"
          >
            <ChevronLeft className="w-4 h-4" />
            Précédent
          </button>

          <button
            onClick={handleNext}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all shadow-md hover:shadow-lg ${
              isLastSlide 
                ? 'bg-gradient-to-r from-[#B5E48C] to-[#98D8C8] text-white hover:scale-105' 
                : 'bg-gradient-to-r from-[#FFB5A7] to-[#FCD5CE] text-white hover:scale-105'
            }`}
            data-testid="onboarding-next"
          >
            {isLastSlide ? (
              <>
                <Check className="w-5 h-5" />
                C'est parti !
              </>
            ) : (
              <>
                Suivant
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
